import type {CheckoutErrorDetail} from '../../transport/types/checkout';

/**
 * Coupon error codes MFL emits at POST /ecommerce/v2/order-validate.
 * The first four are measured; coupon_expired and coupon_invalid_zone are
 * published by MFL but were not reproduced in the DIS-1608 capture run.
 */
const COUPON_CODES = new Set([
  'coupon_spend_more',
  'coupon_spend_more_valid_items',
  'coupon_spend_more_valid_items_over',
  'coupon_not_found',
  'coupon_expired',
  'coupon_invalid_zone',
]);

const COUPON_DETAIL_PATTERN = /coupon/i;

/**
 * The coupon error inside a CheckoutErrorResponse's details[], or null.
 * details[] is the authority — MFL returns several errors and the coupon one is
 * frequently not first, so never branch on the top-level code. Code-less errors
 * are matched on their detail text because MFL omits `code` when a coupon has no
 * configured minimum.
 */
export function findCouponError(details?: CheckoutErrorDetail[]): CheckoutErrorDetail | null {
  return (
    details?.find(d =>
      d.code ? COUPON_CODES.has(d.code) : COUPON_DETAIL_PATTERN.test(d.detail),
    ) ?? null
  );
}

/**
 * Coupon-refusal codes: the coupon itself is not valid, so no amount of shopping
 * fixes it and it must not stay attached to the order. Everything else — the
 * spend_more family, and code-less refusals (region D, no configured minimum) —
 * is a basket refusal, which the shopper resolves by adding qualifying items.
 */
const COUPON_REFUSAL_CODES = new Set(['coupon_not_found', 'coupon_expired', 'coupon_invalid_zone']);

export const isCouponRefusalCode = (code?: string): boolean =>
  !!code && COUPON_REFUSAL_CODES.has(code);
